import type { SupportCase } from "./api";

const TERMINAL_SKIP_STATUSES = new Set<SupportCase["status"]>([
  "skipped",
  "rejected",
  "resolved",
]);

const concise = (value: string | undefined, fallback: string) => {
  const normalized = value?.trim().replace(/\s+/g, " ");
  return normalized ? normalized.slice(0, 140) : fallback;
};

/** A saved skip is routine mail only after its investigation has reached a terminal state. */
export const isOtherEmail = (supportCase: SupportCase): boolean =>
  supportCase.disposition === "skip" && TERMINAL_SKIP_STATUSES.has(supportCase.status);

/** Only the backend's validated, saved fix-sent closure can suppress an alert. */
export const isFixSentClosure = (supportCase: SupportCase): boolean =>
  supportCase.status === "resolved" &&
  supportCase.disposition === "fix_sent" &&
  supportCase.closureReason === "fix_sent";

export const isClosedSupportCase = (supportCase: SupportCase): boolean =>
  !isOtherEmail(supportCase) && ["resolved", "rejected", "skipped"].includes(supportCase.status);

/** Cases that need an operator decision now, rather than a later worker check or customer reply. */
export const needsSupportAttention = (supportCase: SupportCase): boolean => {
  if (isOtherEmail(supportCase) || isFixSentClosure(supportCase)) return false;
  if (supportCase.status === "failed" || supportCase.status === "draft_ready" || supportCase.status === "approved_for_copy") {
    return true;
  }
  if (supportCase.status === "triaged") {
    return supportCase.disposition !== "waiting_customer" || Boolean(supportCase.resolutionReviewRequired);
  }
  return supportCase.status === "escalated" && (
    supportCase.development?.status === "findings_ready" || supportCase.development?.status === "failed"
  );
};

export const supportCaseActivity = (supportCase: SupportCase) => {
  const subject = concise(
    supportCase.source.subject || supportCase.source.senderLabel,
    "Email without a subject"
  );
  return isOtherEmail(supportCase)
    ? { label: subject, outcome: "Other email", attention: false }
    : isFixSentClosure(supportCase)
    ? {
        label: subject,
        outcome: "Fix sent; retry requested",
        detail: "A fix was communicated. New replies are checked again.",
        attention: false,
      }
    : supportCase.status === "failed"
    ? {
        label: subject,
        outcome: "Investigation failed",
        detail: "Retry investigation to review this conversation.",
        attention: true,
      }
    : supportCase.status === "resolved"
    ? {
        label: subject,
        outcome: "Resolved",
        detail: "The resolution was recorded for this case.",
        attention: false,
      }
    : supportCase.status === "queued"
    ? {
        label: subject,
        outcome: "Investigation queued",
        detail: "The investigation is waiting to start.",
        attention: true,
      }
    : supportCase.status === "investigating"
    ? {
        label: subject,
        outcome: "Investigation in progress",
        detail: "Check back after the investigation completes.",
        attention: true,
      }
    : supportCase.status === "waiting_customer" ||
      (supportCase.status === "triaged" && supportCase.disposition === "waiting_customer")
    ? {
        label: subject,
        outcome: "Waiting for customer",
        detail: "Review the case when new information is available.",
        attention: true,
      }
    : supportCase.status === "triaged" && supportCase.resolutionReviewRequired
    ? {
        label: subject,
        outcome: "Resolution needs review",
        detail: "Review the evidence, then resolve only if you confirm it.",
        attention: true,
      }
    : supportCase.status === "draft_ready"
    ? {
        label: subject,
        outcome: "Draft ready for review",
        detail: "Review and edit the internal draft before approval.",
        attention: true,
      }
    : supportCase.status === "approved_for_copy"
    ? {
        label: subject,
        outcome: "Draft approved to copy",
        detail: "Copying it does not send a reply.",
        attention: true,
      }
    : supportCase.development?.status === "findings_ready"
    ? {
        label: subject,
        outcome: "Development findings ready",
        detail: "Review the development findings.",
        attention: true,
      }
    : supportCase.development?.status === "failed"
    ? {
        label: subject,
        outcome: "Development review unavailable",
        detail: "Review the case and decide the next step.",
        attention: true,
      }
    : supportCase.development?.status === "investigating" || supportCase.status === "development_investigating"
    ? {
        label: subject,
        outcome: "Development investigation in progress",
        detail: "Review development findings when they are ready.",
        attention: true,
      }
    : supportCase.development?.status === "queued"
    ? {
        label: subject,
        outcome: "Development review queued",
        detail: "Development review is waiting to start.",
        attention: true,
      }
    : supportCase.status === "escalated" || supportCase.status === "development_pending"
    ? {
        label: subject,
        outcome: "Escalated to development",
        detail: "Development review is pending.",
        attention: true,
      }
    : supportCase.status === "rejected"
    ? {
        label: subject,
        outcome: "Case rejected",
        detail: "Review the findings before deciding the next step.",
        attention: true,
      }
    : {
        label: subject,
        outcome: "Ready for review",
        detail: "Review the findings and choose the next action.",
        attention: true,
      };
};
